import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import { useAction, updateAccount } from "wasp/client/operations";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../client/components/ui/dialog";
import { Button } from "../../client/components/ui/button";
import { useToast } from "../../client/hooks/use-toast";
import useTranslation from "../../client/hooks/useTranslation";
import type { AccountViolation } from "../../trading-journal/operations";

type AccountBlewUpModalProps = {
  open: boolean;
  violation: AccountViolation | null;
  onConfirm: () => void;
  onDismiss: () => void;
};

export function AccountBlewUpModal({
  open,
  violation,
  onConfirm,
  onDismiss,
}: AccountBlewUpModalProps) {
  const t = useTranslation();
  const { toast } = useToast();
  const updateAccountFn = useAction(updateAccount);
  const [isLoading, setIsLoading] = useState(false);

  if (!violation) return null;

  const subtitleTemplate =
    violation.violationType === "daily_loss"
      ? t.tradingAccounts.blewUpModal.subtitleDailyLoss
      : violation.violationType === "total_loss"
        ? t.tradingAccounts.blewUpModal.subtitleTotalLoss
        : t.tradingAccounts.blewUpModal.subtitleTrailingDrawdown;

  // Replace {accountName} with the real name, then render **name** as bold
  const subtitle = subtitleTemplate.replace("{accountName}", violation.accountName);

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      await updateAccountFn({
        id: violation.accountId,
        status: "inactive",
        isActive: false,
        disabledAt: new Date().toISOString(),
        // Marqueur explicite : distingue un compte réellement cramé d'un compte
        // simplement archivé. Exploité par les rapports périodiques, qui ne
        // comptent en haute confiance que "blown" et "drawdown_breach".
        deactivatedReason: "blown",
      });
      onConfirm();
    } catch (error: any) {
      toast({
        title: t.common.error,
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-6 w-6 text-destructive shrink-0" />
            <DialogTitle>{t.tradingAccounts.blewUpModal.title}</DialogTitle>
          </div>
        </DialogHeader>
        <div className="py-2 space-y-2">
          <p className="text-sm text-muted-foreground">
            {/* Render subtitle: text between ** is displayed in bold */}
            {subtitle.split("**").map((part, i) =>
              i % 2 === 1 ? (
                <strong key={i} className="text-foreground">
                  {part}
                </strong>
              ) : (
                part
              ),
            )}
          </p>
          <p className="text-sm font-medium">{t.tradingAccounts.blewUpModal.question}</p>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onDismiss} disabled={isLoading}>
            {t.tradingAccounts.blewUpModal.dismiss}
          </Button>
          <Button variant="destructive" onClick={handleConfirm} disabled={isLoading}>
            {t.tradingAccounts.blewUpModal.confirm}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
